import { FanguardApiContractsClientVenueRequestModel } from 'src/api/generated';
import { patch, post, remove } from '@/api/api';
import { venuesQueries, VENUES_URI } from '@/api/queryKeys';
import { useQuery } from '@tanstack/react-query';
import { useMutationWithMessages } from './helpers';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import queryClient from 'src/queryClient';

const useGetVenues = () => {
  const query = useQuery({
    ...venuesQueries.list,
    retry: (failureCount: number, error: unknown) => {
      if (error instanceof AxiosError && error.response?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });

  useEffect(() => {
    if (query.isError) {
      queryClient.removeQueries({ queryKey: venuesQueries.list.queryKey });
    }
  }, [query.isError]);

  return query;
};

const useGetVenue = (id?: string) => {
  return useQuery({
    ...venuesQueries.detail(id ?? ''),
    enabled: !!id,
  });
};

const useCreateVenue = () => {
  return useMutationWithMessages(
    (data: FanguardApiContractsClientVenueRequestModel) => post({ uri: VENUES_URI, data }),
    {
      entityType: 'venue',
      action: 'create',
      queryKeysToInvalidate: [venuesQueries.list._def],
    },
  );
};

const useUpdateVenue = (id: string) => {
  return useMutationWithMessages(
    (data: FanguardApiContractsClientVenueRequestModel) => patch({ uri: `${VENUES_URI}/${data.id}`, data }),
    {
      entityType: 'venue',
      action: 'update',
      queryKeysToInvalidate: [venuesQueries.list._def, venuesQueries.detail(id).queryKey],
    },
  );
};

const useUploadVenueImage = () => {
  return useMutationWithMessages(
    (data: FanguardApiContractsClientVenueRequestModel) => patch({ uri: `${VENUES_URI}/${data.id}`, data }),
    {
      entityType: 'image',
      action: 'upload',
      queryKeysToInvalidate: [venuesQueries.list._def],
    },
  );
};

const useDeleteVenueImage = () => {
  return useMutationWithMessages(
    (data: FanguardApiContractsClientVenueRequestModel) => patch({ uri: `${VENUES_URI}/${data.id}`, data }),
    {
      entityType: 'image',
      action: 'delete',
      queryKeysToInvalidate: [venuesQueries.list._def],
    },
  );
};

const useDeleteVenue = () => {
  return useMutationWithMessages((venueId: string) => remove({ uri: `${VENUES_URI}/${venueId}` }), {
    entityType: 'venue',
    action: 'delete',
    queryKeysToInvalidate: [venuesQueries.list._def],
  });
};

export {
  useGetVenues,
  useGetVenue,
  useCreateVenue,
  useUpdateVenue,
  useDeleteVenue,
  useUploadVenueImage,
  useDeleteVenueImage,
};
